import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

interface HeaderProps {
  currentPath: string;
}

const Header: React.FC<HeaderProps> = ({ currentPath }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleServiciosClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (currentPath !== '/') {
      navigate('/');
      setTimeout(() => {
        document.getElementById('pilar')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      document.getElementById('pilar')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const isActive = (path: string) => currentPath === path;

  const navLinks = [
    { path: '/soluciones', label: 'Soluciones' },
    { path: '#servicios', label: 'Servicios', onClick: handleServiciosClick },
    { path: '/nosotros', label: 'Nosotros' },
    { path: '/blog', label: 'Blog Digital', hasBadge: true },
  ];

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'py-4 bg-[#050505]/80 backdrop-blur-md border-b border-white/10' : 'py-6 bg-transparent'
    }`}>
      <div className="container mx-auto px-6 flex items-center justify-between">
        <Link to="/" className="flex items-center">
          <img
            src="https://lqpglobal.com/logocubo.png"
            alt="LQP Global - Soluciones digitales para pymes"
            className="h-10 md:h-12 w-auto object-contain transition-transform hover:scale-105"
          />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-gray-400" aria-label="Navegación principal">
          {navLinks.map((link) => (
            link.onClick ? (
              <a
                key={link.path}
                href={link.path}
                onClick={link.onClick}
                className="hover:text-white transition-colors"
              >
                {link.label}
              </a>
            ) : (
              <Link
                key={link.path}
                to={link.path}
                className={`transition-colors hover:text-white ${
                  isActive(link.path) ? 'text-white font-bold' : ''
                } ${link.hasBadge ? 'flex items-center gap-2' : ''}`}
              >
                {link.hasBadge && (
                  <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" aria-hidden="true"></span>
                )}
                {link.label}
              </Link>
            )
          ))}
          <Link
            to="/contacto"
            className={`px-5 py-2 rounded-full glass hover:bg-white/10 transition-all text-white border-white/20 font-bold ${
              isActive('/contacto') ? 'bg-white/10 border-white' : ''
            }`}
          >
            Contacto
          </Link>
        </nav>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden text-white p-2"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label={isMobileMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
          aria-expanded={isMobileMenuOpen}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {isMobileMenuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
            )}
          </svg>
        </button>
      </div>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <nav
          className="md:hidden absolute top-full left-0 right-0 bg-[#050505]/95 backdrop-blur-md border-b border-white/10 py-6"
          aria-label="Navegación móvil"
        >
          <div className="container mx-auto px-6 flex flex-col gap-4">
            {navLinks.map((link) => (
              link.onClick ? (
                <a
                  key={link.path}
                  href={link.path}
                  onClick={(e) => {
                    link.onClick(e);
                    setIsMobileMenuOpen(false);
                  }}
                  className="text-gray-400 hover:text-white transition-colors py-2"
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`py-2 transition-colors hover:text-white ${
                    isActive(link.path) ? 'text-white font-bold' : 'text-gray-400'
                  } ${link.hasBadge ? 'flex items-center gap-2' : ''}`}
                >
                  {link.hasBadge && (
                    <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" aria-hidden="true"></span>
                  )}
                  {link.label}
                </Link>
              )
            ))}
            <Link
              to="/contacto"
              onClick={() => setIsMobileMenuOpen(false)}
              className="mt-4 px-5 py-3 rounded-full glass text-center text-white border-white/20 font-bold"
            >
              Contacto
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
};

export default Header;
